import {
  useMutation,
  useQueryClient,
  UseMutationResult,
} from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import { NOTIFICATION } from "@via/context/notifications-context.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { ErrorNotification } from "@via/utils/interfaces.tsx";

/**
 * Importing processes from a git repository: the batch import wizard's two calls into the
 * backend, scanning a repository for every process revision it publishes
 * (`import-scan-service.ts`) and importing a selection of them under one consent
 * (`import-batch-service.ts`).
 *
 * These types are declared here rather than in `common`, matching `script-assets.api.tsx`'s own
 * rule: nothing outside this feature consumes them yet, and a shared type would force a frontend
 * rebuild gate for no benefit.
 */

/**
 * Matches `processGitImportCheckBodySchema` in `backend/src/routes/process.ts`, the single
 * revision check. No hook here calls that route any more, but the wizard's Source step still takes
 * its provider union from this type (`source-step.tsx`), so it stays.
 */
export type ProcessImportCheckBody = {
  repoUrl: string;
  provider: "github" | "gitlab" | "bitbucket";
  serviceTokenId?: number | null;
  repoPath?: string;
  revision?: number;
};

/**
 * Matches `processGitImportScanBodySchema` in `backend/src/routes/process.ts`. There is no
 * `repoPath` or `revision`: a scan always enumerates.
 */
export type ProcessImportScanBody = {
  repoUrl: string;
  provider: "github" | "gitlab" | "bitbucket";
  serviceTokenId?: number | null;
  /** Defaults to "latest" on the server if omitted. */
  revisions?: "latest" | "all";
};

/**
 * The six statuses a scan row's classifier verdict can carry, from `import-candidate-status.ts`.
 * Only `new` is selectable in the picker.
 */
export type ImportCandidateStatus =
  | "unreadable"
  | "contract-refused"
  | "uuid-conflict"
  | "already-imported"
  | "update-available"
  | "new";

/**
 * Mirrors `ProcessImportScanRow` in `backend/src/business/process/git/import-scan-service.ts`,
 * and `processImportScanRowSchema` in `routes/process.ts`. One row per process revision pair: a
 * repository scanned with `revisions: "all"` returns one row per published revision, not one per
 * process, so two rows CAN share the same `repoPath` (a repository can publish `rev-0` beside
 * `rev-1` of the same process). Never assume `repoPath` alone is unique across rows; pair it with
 * `revId`.
 */
export type ProcessImportScanRow = {
  repoPath: string;
  /** From the parsed contract, bounded to a display length; null when process.yaml could not be read. */
  name: string | null;
  /** From the parsed contract, bounded to a display length; null when process.yaml could not be read. */
  summary: string | null;
  /** The revision this row describes. */
  revId: number;
  /** Every revision this process publishes, ascending. */
  revisions: number[];
  inputCount: number | null;
  outputCount: number | null;
  fidelityLevel: string | null;
  assetCount: number | null;
  /** The commit this revision's tag points at, never the tag object. */
  commitId: string;
  /** The commit's committer date, strict ISO 8601. */
  committedAt: string | null;
  /** Null unless the contract parsed and is importable. */
  contentHash: string | null;
  /** The Foundry-Revision-Uuid trailer, when the commit carries one. */
  revisionUuid: string | null;
  /** The Foundry-Process-Uuid trailer, when the commit carries one. */
  processUuid: string | null;
  /** This revision's import status against this installation; only "new" is importable. */
  status: ImportCandidateStatus;
  /** Why, as a sentence, composed by the classifier. Null for "new". */
  statusReason: string | null;
  /** The process already here, for uuid-conflict, already-imported, and update-available when its family has a live row. */
  existingProcessId: number | null;
  /** The matched process family; update-available only. */
  existingProcessGid: number | null;
};

/** The HTTP response body of `POST /v1/process/git/import/scan`. */
export type ProcessImportScanReport = {
  rows: ProcessImportScanRow[];
  /** MAX_BATCH_IMPORT, read from server config, so the picker's cap never drifts from it. */
  maxBatchImport: number;
};

/**
 * One process revision to import inside a batch. Matches the `items` entries in
 * `processGitImportBatchBodySchema`.
 */
export type ProcessImportBatchItem = {
  repoPath: string;
  revision: number;
  /** The commit id the scan reported for this revision. The item is skipped if the repository no longer agrees. */
  expectedCommitId: string;
  /** Overrides the batch's own `menuGroupId` for this one process. */
  menuGroupId?: number;
};

/**
 * Matches `processGitImportBatchBodySchema`. The repository, provider and credential fields are
 * stated again rather than extended from the scan body, matching the schema's own comment: no
 * single process `repoPath` or `revision` belongs at the top level.
 */
export type ProcessImportBatchBody = {
  repoUrl: string;
  provider: "github" | "gitlab" | "bitbucket";
  serviceTokenId?: number | null;
  /** The process menu group every imported process goes under, unless its own item names one. */
  menuGroupId: number;
  /** Consent to importing untrusted, unreviewed code, for every revision in items. The server accepts only true. */
  confirmImport: true;
  items: ProcessImportBatchItem[];
};

/** skipped is a correct refusal (a vanished tag, a moved commit, a status no longer new); failed is anything else. */
export type ProcessImportBatchOutcome = "imported" | "skipped" | "failed";

/** One outcome per requested item, in request order. Mirrors `processImportBatchResultSchema`. */
export type ProcessImportBatchItemResult = {
  repoPath: string;
  revision: number;
  outcome: ProcessImportBatchOutcome;
  /** The process created; null unless imported. */
  processId: number | null;
  /** Its process family; null unless imported. */
  processGid: number | null;
  /** The imported process's name; null unless imported. */
  name: string | null;
  /** Why, for skipped and failed; null for imported. */
  message: string | null;
};

/** The HTTP response body of `POST /v1/process/git/import/batch`. */
export type ProcessImportBatchResult = {
  /** Fresh per request, and carried on every audit line this batch wrote. */
  batchId: string;
  results: ProcessImportBatchItemResult[];
};

const detailOf = (error: ErrorNotification, fallback: string): string =>
  error?.response?.data?.message ?? fallback;

/**
 * What a batch that got NO response says. The browser's request timeout (`common/api/axios.tsx`)
 * or a dropped connection ends the request on this page while the server keeps importing, so
 * nothing here knows which processes were imported, and "could not be imported" may be false.
 * Shared by the batch hook's toast and the wizard's inline message, so the two never differ.
 */
export const BATCH_NO_ANSWER_MESSAGE =
  "Foundry did not answer in time, so this page cannot tell which processes were imported. Some may already be imported. Scan the repository again to see which.";

/**
 * True when a request failed with no response from the server at all: a timeout or a dropped
 * connection. Not the upgrade required refusal `axios.tsx` raises in place of a 469, which is an
 * answer.
 */
export const hasNoResponse = (error: unknown): boolean =>
  null !== error &&
  "object" === typeof error &&
  undefined === (error as { response?: unknown }).response &&
  true !== (error as { isUpgradeRequired?: unknown }).isUpgradeRequired;

/**
 * Scans a repository for every process revision it publishes and each one's import status
 * against this installation.
 *
 * A mutation, not a query: this reaches the network on the server side (`ls-remote`, a fetch, a
 * batched trailer read, up to `MAX_SCAN_ROWS` blob reads) against a remote the caller names, so
 * caching it under a query key would serve a stale answer about a repository the server has not
 * re-scanned. It invalidates nothing on success: a scan is a read, and nothing else in the app
 * depends on its result.
 */
export const useProcessGitImportScan = (): UseMutationResult<
  ProcessImportScanReport,
  ErrorNotification,
  ProcessImportScanBody
> => {
  const { setNotification } = useNotifications();

  return useMutation<
    ProcessImportScanReport,
    ErrorNotification,
    ProcessImportScanBody
  >({
    mutationFn: async body => {
      const res = await axios.post("/api/v1/process/git/import/scan", body);
      return res.data as ProcessImportScanReport;
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "process-git-import-scan-error",
        message: "Scan failed",
        details: detailOf(error, "The repository could not be scanned."),
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

/**
 * Imports a selection of checked process revisions from one repository under one consent.
 *
 * Invalidates the pipeline builder's process list whenever the batch settles, the exact same
 * query key and `refetchType` that every sibling process mutation invalidates
 * (`useSaveNewProcess`, `useSaveProcessRevision`, `useDuplicateProcess`, `useOverwriteRevision`,
 * `useDeleteProcessRevision`, all in `pipelines.api.tsx`). Without this, processes a batch
 * imports are absent from the pipeline builder sidebar the user is looking at until a full page
 * reload. On an error too, not only on success: a batch the browser stopped waiting for keeps
 * importing on the server, so an error here never proves that nothing was imported.
 */
export const useProcessGitImportBatch = (): UseMutationResult<
  ProcessImportBatchResult,
  ErrorNotification,
  ProcessImportBatchBody
> => {
  const { setNotification } = useNotifications();
  const queryClient = useQueryClient();

  return useMutation<
    ProcessImportBatchResult,
    ErrorNotification,
    ProcessImportBatchBody
  >({
    mutationFn: async body => {
      const res = await axios.post("/api/v1/process/git/import/batch", body);
      return res.data as ProcessImportBatchResult;
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/project/v1/pipeline-builder-node"],
        refetchType: "all",
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification(
        hasNoResponse(error)
          ? {
              id: "process-git-import-batch-error",
              message: "Import status unknown",
              details: BATCH_NO_ANSWER_MESSAGE,
              variant: NOTIFICATION.ERROR,
            }
          : {
              id: "process-git-import-batch-error",
              message: "Import failed",
              details: detailOf(error, "The batch could not be imported."),
              variant: NOTIFICATION.ERROR,
            }
      );
    },
  });
};
